/**
 * Canvas Types
 *
 * Type definitions for the Full-Mockup Canvas system.
 * Used by CanvasEditor and related components.
 */

import type { MockupBlendMode } from '@/api/products';

// =============================================================================
// CANVAS CONFIGURATION
// =============================================================================

/**
 * Configuration for the canvas editor.
 * Defines mockup dimensions, print area, and layer URLs.
 */
export interface CanvasConfig {
  // Mockup dimensions (= Canvas native size)
  mockupWidth: number;
  mockupHeight: number;

  // Print-Area within the mockup: the template's rectangle (templateRect)
  printArea: {
    /** Left edge, pixels from the canvas left */
    x: number;
    /** Top edge, pixels from the canvas top */
    y: number;
    /** Print area width in pixels */
    width: number;
    /** Print area height in pixels */
    height: number;
  };

  // Mockup layer URLs
  mockupImageUrl: string;
  /**
   * Opacity of the mockup image. It is a shading map that sits ABOVE the
   * design per `layerOrder` (Shirtigo "Deckkraft Schatten-Layer"); below the
   * design only for embroidery.
   */
  baseOpacity?: number;
  /** Render order of the orderable stack, bottom → top (see @designdeck/shared) */
  layerOrder?: string[];
  backgroundColor: string;

  // Optional overlay URLs
  heatheredOverlayUrl?: string | null;
  textureOverlayUrl?: string | null;
  /** Pinned above the ordered stack, below the cutout mask */
  shadowOverlayUrl?: string | null;
  /** Overlay above the whole stack and above the cutout mask (never clipped) */
  topOverlayUrl?: string | null;
  cutoutMaskUrl?: string | null;
  /**
   * Garment parts that hang in front of the print (hood drawstrings): a
   * luminance mask over the mockup. Drawn above the design, inside the
   * cutout — like MockupRenderer and the print render do.
   */
  toplayerMaskUrl?: string | null;
  /**
   * Draw the dashed print-area border as a Fabric rect. Defaults to true.
   * A host that frames the print area itself (the guided frame draws a CSS
   * border over the canvas, identical to its still preview) turns it off so
   * the two do not stack.
   */
  printAreaBorder?: boolean;

  // Overlay settings
  heatheredBlendMode?: MockupBlendMode;
  heatheredOpacity?: number;
  textureBlendMode?: MockupBlendMode;
  textureOpacity?: number;
  shadowBlendMode?: MockupBlendMode;
  shadowOpacity?: number;

  // Flags
  isHeathered?: boolean;
}

// =============================================================================
// CANVAS SIZING
// =============================================================================

/**
 * Configuration for responsive canvas sizing.
 */
export interface CanvasSizeConfig {
  /** Native canvas width in pixels */
  nativeWidth: number;
  /** Native canvas height in pixels */
  nativeHeight: number;
  /** Padding around canvas in container (default: 32) */
  padding?: number;
  /** Maximum scale factor (default: 2) */
  maxScale?: number;
  /** Minimum scale factor (default: 0.5) */
  minScale?: number;
}

/**
 * Result of canvas size calculation.
 */
export interface CanvasSizeResult {
  /** Display width after scaling */
  displayWidth: number;
  /** Display height after scaling */
  displayHeight: number;
  /** Current scale factor */
  scaleFactor: number;
  /** Native canvas width */
  nativeWidth: number;
  /** Native canvas height */
  nativeHeight: number;
}

// =============================================================================
// CANVAS LAYERS
// =============================================================================

// Stufe 4.2: SYSTEM_LAYER_NAMES + isSystemLayer are now re-exported from @designdeck/shared
// via @/services/canvas/systemLayerManager.ts (Single Source of Truth).
// This file re-exports for backwards compatibility with existing imports.
export { SYSTEM_LAYER_NAMES, type SystemLayerName } from '@designdeck/shared';

/**
 * Check if a layer name is a system layer.
 */
export function isSystemLayer(name: string | undefined): boolean {
  if (!name) return false;
  return name.startsWith('__');
}

// =============================================================================
// PRINT AREA
// =============================================================================

/**
 * Print area coordinates and dimensions.
 */
export interface PrintArea {
  /** X position from canvas left */
  x: number;
  /** Y position from canvas top */
  y: number;
  /** Width in pixels */
  width: number;
  /** Height in pixels */
  height: number;
}
